import { useEffect, useState } from 'react';

export type LiveNotificationKind = 'order' | 'assign';

export interface LiveNotification {
  id: string;
  kind: LiveNotificationKind;
  title: string;
  note: string;
  orderNo: string;
}

interface LiveNotificationsProps {
  notifications: LiveNotification[];
  onDismiss: (id: string) => void;
  onAction: (n: LiveNotification) => void;
  duration?: number;
}

const kindStyles: Record<
  LiveNotificationKind,
  { icon: string; iconWrap: string; actionLabel: string; bar: string }
> = {
  order: {
    icon: 'ri-inbox-archive-line',
    iconWrap: 'bg-success-100 text-success-600',
    actionLabel: 'قبول الطلب',
    bar: 'bg-success-500',
  },
  assign: {
    icon: 'ri-user-add-line',
    iconWrap: 'bg-warning-100 text-warning-700',
    actionLabel: 'عيّن مندوب',
    bar: 'bg-warning-500',
  },
};

interface NotificationItemProps {
  n: LiveNotification;
  duration: number;
  onDismiss: (id: string) => void;
  onAction: (n: LiveNotification) => void;
}

function NotificationItem({ n, duration, onDismiss, onAction }: NotificationItemProps) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => onDismiss(n.id), 350);
    return () => clearTimeout(timer);
  }, [leaving, n.id, onDismiss]);

  const s = kindStyles[n.kind];

  return (
    <div className={leaving ? 'animate-notif-out' : 'animate-notif-in'}>
      <div className="relative w-full overflow-hidden rounded-2xl border border-background-200 bg-white shadow-soft">
        <div className="flex items-start gap-3 p-3.5">
          <span
            className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${s.iconWrap}`}
          >
            <i className={`${s.icon} text-[20px] leading-none`} aria-hidden="true"></i>
            <span className="absolute -end-0.5 -top-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-500 opacity-75"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success-500"></span>
            </span>
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-[13px] text-foreground-950">{n.title}</p>
              <span className="whitespace-nowrap text-[10px] text-foreground-400">الآن</span>
            </div>
            <p className="mt-0.5 text-[12px] leading-relaxed text-foreground-600">{n.note}</p>
            <button
              type="button"
              onClick={() => onAction(n)}
              className="mt-2.5 inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-secondary-500 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-secondary-600"
            >
              {s.actionLabel}
              <i className="ri-arrow-left-line text-[13px]" aria-hidden="true"></i>
            </button>
          </div>

          <button
            type="button"
            onClick={() => onDismiss(n.id)}
            className="cursor-pointer text-foreground-300 transition-colors hover:text-foreground-600"
            aria-label="إغلاق"
          >
            <i className="ri-close-line text-base" aria-hidden="true"></i>
          </button>
        </div>

        <div className="h-0.5 w-full bg-background-100">
          <div
            className={`h-full ${s.bar}`}
            style={{ animation: `notifBar ${duration}ms linear forwards` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function LiveNotifications({
  notifications,
  onDismiss,
  onAction,
  duration = 6000,
}: LiveNotificationsProps) {
  if (notifications.length === 0) return null;

  return (
    <div className="pointer-events-none fixed end-4 top-20 z-[90] flex w-[300px] flex-col gap-3 sm:end-6 sm:w-[320px]">
      {notifications.map((n) => (
        <div key={n.id} className="pointer-events-auto">
          <NotificationItem n={n} duration={duration} onDismiss={onDismiss} onAction={onAction} />
        </div>
      ))}
    </div>
  );
}